import React, { useEffect } from 'react';
import type { SyntheticEvent } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Image from 'next/image';

export type WarningModalType = 'info' | 'warning' | 'error' | 'success';

interface WarningModalProps {
  open: boolean;
  onClose: () => void;
  type?: WarningModalType; // icon and color
  title?: string;
  message?: string;
  primaryButtonText?: string;
  onPrimary?: () => void;
  secondaryButtonText?: string;
  onSecondary?: () => void;
  secondaryButtonBackgroundColor?: string; // Background color for secondary button
  secondaryButtonBorderColor?: string; // Border color for secondary button
  secondaryButtonTextColor?: string; // Text color for secondary button
  secondaryButtonHoverBackgroundColor?: string; // Hover background color for secondary button
  secondaryButtonHoverBorderColor?: string; // Hover border color for secondary button
  secondaryButtonDisabledBackgroundColor?: string; // Disabled background color for secondary button
  secondaryButtonDisabledBorderColor?: string; // Disabled border color for secondary button
  secondaryButtonDisabledTextColor?: string; // Disabled text color for secondary button
  secondaryButtonPosition?: 'left' | 'right'; // Position of secondary button (default: 'right')
  tertiaryButtonText?: string;
  onTertiary?: () => void;
  remainingTime?: string; // New prop for countdown display
  isCritical?: boolean; // New prop for critical state
  zIndex?: number; // Custom z-index for the modal
  primaryButtonDisabled?: boolean; // Disable primary button (e.g., during async operations)
}

const ICONS: Record<WarningModalType, { src: string; alt: string; color: string }> = {
  info:    { src: '/warning/info.svg',    alt: 'Info',    color: '#153C7F' },
  warning: { src: '/warning/warning.svg', alt: 'Warning', color: '#B89B00' },
  error:   { src: '/warning/error.svg',   alt: 'Error',   color: '#F44336' },
  success: { src: '/warning/check.svg',   alt: 'Success', color: '#63A821' },
};

const DEFAULTS: Record<WarningModalType, { title: string; primary: string; }> = {
  info:    { title: 'Just So You Know', primary: 'OK' },
  warning: { title: 'Just So You Know', primary: 'OK' },
  error:   { title: 'Something Went Wrong', primary: 'Retry' },
  success: { title: 'Success', primary: 'OK' },
};

const WarningModal: React.FC<WarningModalProps> = ({
  open,
  onClose,
  type = 'info',
  title,
  message,
  primaryButtonText,
  onPrimary,
  secondaryButtonText,
  onSecondary,
  secondaryButtonBackgroundColor = '#F3F1FF',
  secondaryButtonBorderColor = '#F3F1FF',
  secondaryButtonTextColor = '#767C88',
  secondaryButtonHoverBackgroundColor = '#E8E5FF',
  secondaryButtonHoverBorderColor = '#E8E5FF',
  secondaryButtonDisabledBackgroundColor = '#F3F1FF',
  secondaryButtonDisabledBorderColor = '#F3F1FF',
  secondaryButtonDisabledTextColor = '#9CA3AF',
  secondaryButtonPosition = 'right',
  tertiaryButtonText,
  onTertiary,
  remainingTime,
  isCritical = false,
  zIndex,
  primaryButtonDisabled = false,
}) => {
  const icon = ICONS[type] || ICONS.info;
  const defaultTitle = title || DEFAULTS[type].title;
  const defaultPrimary = primaryButtonText || DEFAULTS[type].primary;

  // Check if time is running low (less than 2 minutes)
  const isTimeRunningLow = remainingTime && remainingTime.startsWith('00:') && parseInt(remainingTime.split(':')[1]) < 2;

  // Determine colors based on critical state
  const getCountdownColors = () => {
    if (isCritical) {
      return {
        background: '#FFEBEE',
        border: '#F44336',
        text: '#F44336',
      };
    } else if (isTimeRunningLow) {
      return {
        background: '#FFF3CD',
        border: '#FF9800',
        text: '#FF9800',
      };
    } else {
      return {
        background: '#FFF3CD',
        border: '#B89B00',
        text: '#B89B00',
      };
    }
  };

  const colors = getCountdownColors();

  // Set z-index on Dialog portal container when open and zIndex is provided
  useEffect(() => {
    if (open && zIndex) {
      // Use setTimeout to ensure Dialog is rendered in the DOM
      const timeoutId = setTimeout(() => {
        // Find all Dialog containers and select the last one (most recently added)
        const dialogContainers = document.querySelectorAll('.MuiDialog-root');
        const backdrops = document.querySelectorAll('.MuiBackdrop-root');
        
        // Get the last Dialog container (should be this one)
        const dialogContainer = dialogContainers[dialogContainers.length - 1] as HTMLElement;
        const backdrop = backdrops[backdrops.length - 1] as HTMLElement;
        
        if (dialogContainer) {
          dialogContainer.style.zIndex = String(zIndex);
        }
        if (backdrop) {
          backdrop.style.zIndex = String(zIndex - 1);
        }
      }, 0);

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [open, zIndex]);

  const handleDialogClose = (event: SyntheticEvent, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (reason === 'backdropClick') {
      return;
    }
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleDialogClose} 
      maxWidth="xs" 
      fullWidth
      sx={zIndex ? {
        zIndex: zIndex,
        '& .MuiDialog-container': {
          zIndex: zIndex,
        },
      } : undefined}
      PaperProps={zIndex ? {
        sx: {
          zIndex: zIndex,
        }
      } : undefined}
      BackdropProps={zIndex ? {
        sx: {
          zIndex: zIndex - 1,
        }
      } : undefined}
    >
      <DialogContent
        sx={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          pt: 5, pb: 4, px: 4,
        }}
      >
        <div style={{
          background: '#fff',
          borderRadius: '50%',
          width: 120, height: 120,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: 24,
          boxShadow: '0 2px 8px rgba(21, 60, 127, 0.08)',
        }}>
          <Image src={icon.src} alt={icon.alt} width={72} height={72} />
        </div>
        <DialogTitle
          sx={{
            textAlign: 'center',
            fontWeight: 700,
            fontSize: 24,
            color: icon.color,
            p: 0,
            mb: 1,
            fontFamily: 'Poppins, sans-serif',
          }}
        >
          {defaultTitle}
        </DialogTitle>
        {remainingTime && (
          <div style={{
            background: colors.background,
            border: `2px solid ${colors.border}`,
            borderRadius: '12px',
            padding: '16px 24px',
            marginBottom: 16,
            textAlign: 'center',
            fontFamily: 'Poppins, sans-serif',
            animation: (isTimeRunningLow || isCritical) ? 'pulse 1s infinite' : 'none',
          }}>
            <div style={{
              fontSize: 14,
              color: colors.text,
              fontWeight: 600,
              marginBottom: 4,
            }}>
              {isCritical ? 'CRITICAL: Time Remaining' : 'Time Remaining'}
            </div>
            <div style={{
              fontSize: 32,
              fontWeight: 700,
              color: colors.text,
              fontFamily: 'monospace',
              letterSpacing: '2px',
            }}>
              {remainingTime}
            </div>
          </div>
        )}
        {message && (
          <div 
            style={{
              color: '#757575',
              fontSize: 18,
              textAlign: 'center',
              marginBottom: 24,
              fontFamily: 'Poppins, sans-serif',
            }}
            dangerouslySetInnerHTML={{ __html: message }}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ 
        p: 2, 
        justifyContent: secondaryButtonPosition === 'left' ? 'space-between' : 'flex-end', 
        gap: 2 
      }}>
        {secondaryButtonPosition === 'left' && secondaryButtonText && (
          <Button
            onClick={onSecondary || onClose}
            disabled={primaryButtonDisabled}
            sx={{
              backgroundColor: secondaryButtonBackgroundColor,
              color: secondaryButtonTextColor,
              border: `1px solid ${secondaryButtonBorderColor}`,
              borderRadius: '6px',
              fontWeight: 400,
              fontFamily: 'Poppins, sans-serif',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: secondaryButtonHoverBackgroundColor,
                borderColor: secondaryButtonHoverBorderColor,
                transform: 'translateY(-1px)',
              },
              '&:disabled': {
                backgroundColor: secondaryButtonDisabledBackgroundColor,
                color: secondaryButtonDisabledTextColor,
                borderColor: secondaryButtonDisabledBorderColor,
                transform: 'none',
              }
            }}
          >
            {secondaryButtonText}
          </Button>
        )}
        <div style={{ display: 'flex', gap: '16px', marginLeft: secondaryButtonPosition === 'left' ? 'auto' : 0 }}>
          {tertiaryButtonText && (
          <Button
            onClick={onTertiary}
            disabled={primaryButtonDisabled}
            sx={{
              backgroundColor: '#fff',
              color: '#63A821',
              border: '1px solid #63A821',
              borderRadius: '6px',
              fontWeight: 600,
              fontFamily: 'Poppins, sans-serif',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: '#F0F9ED',
                borderColor: '#4e8c1a',
                transform: 'translateY(-1px)',
              },
              '&:disabled': {
                backgroundColor: '#fff',
                color: '#9CA3AF',
                borderColor: '#D1D5DB',
                transform: 'none',
              }
            }}
          >
            {tertiaryButtonText}
          </Button>
          )}
          {secondaryButtonPosition === 'right' && secondaryButtonText && (
            <Button
              onClick={onSecondary || onClose}
              disabled={primaryButtonDisabled}
              sx={{
                backgroundColor: secondaryButtonBackgroundColor,
                color: secondaryButtonTextColor,
                border: `1px solid ${secondaryButtonBorderColor}`,
                borderRadius: '6px',
                fontWeight: 400,
                fontFamily: 'Poppins, sans-serif',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: secondaryButtonHoverBackgroundColor,
                  borderColor: secondaryButtonHoverBorderColor,
                  transform: 'translateY(-1px)',
                },
                '&:disabled': {
                  backgroundColor: secondaryButtonDisabledBackgroundColor,
                  color: secondaryButtonDisabledTextColor,
                  borderColor: secondaryButtonDisabledBorderColor,
                  transform: 'none',
                }
              }}
            >
              {secondaryButtonText}
            </Button>
          )}
          <Button
            variant="contained"
            onClick={onPrimary || onClose}
            disabled={primaryButtonDisabled}
            sx={{
              backgroundColor: '#63A821',
              color: '#fff',
              borderRadius: '6px',
              fontWeight: 600,
              fontFamily: 'Poppins, sans-serif',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: '#4e8c1a',
                transform: 'translateY(-1px)',
              },
              '&:disabled': {
                backgroundColor: '#D1D5DB',
                color: '#9CA3AF',
                transform: 'none',
              }
            }}
          >
            {defaultPrimary}
          </Button>
        </div>
      </DialogActions>
      <style jsx>{`
        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.02); }
          100% { transform: scale(1); }
        }
      `}</style>
    </Dialog>
  );
};

export default WarningModal; 